/**
 * AI Strategic Goals System
 * 
 * Defines goal types and structures for Manager to assign to units
 */

import { type BattleUnit } from '@/core/types/battle.types';

// ═══════════════════════════════════════════════════════════════════════════
// GOAL TYPES
// ═══════════════════════════════════════════════════════════════════════════

export const GoalType = {
    ELIMINATE: 'ELIMINATE',        // Kill specific target
    FOCUS_FIRE: 'FOCUS_FIRE',      // Multiple units attack one target
    LOCKDOWN: 'LOCKDOWN',          // Block/engage target
    ADVANCE: 'ADVANCE',            // Move toward enemy lines
    REPOSITION: 'REPOSITION',            // Move toward enemy lines
    OPPORTUNISTIC: 'OPPORTUNISTIC' // Free choice
} as const;

export type GoalType = typeof GoalType[keyof typeof GoalType];

// ═══════════════════════════════════════════════════════════════════════════
// GOAL INTERFACE
// ═══════════════════════════════════════════════════════════════════════════

export interface Goal {
    id: string;                  // Unique identifier
    type: GoalType;             // What to do
    priority: number;           // 1-10 (higher = more important)
    
    // Target info
    targetId?: string;          // Enemy unitId to focus on
    targetPosition?: string;  // Or position to reach
    // targetPosition?: { q: number; r: number };  // Or position to reach
    
    // Assignment constraints
    minUnits?: number;          // Min units needed (for FOCUS_FIRE)
    maxUnits?: number;          // Max units allowed
    assignedUnits: string[];    // Units currently pursuing this goal
    
    // Validity
    isActive: boolean;          // Can this goal still be pursued?
    reason?: string;            // Why goal was created/invalidated
}

// ═══════════════════════════════════════════════════════════════════════════
// GOAL EVALUATION CRITERIA
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Function type for evaluating how suitable a unit is for a goal
 */
export type GoalSuitabilityEvaluator = (
    unit: BattleUnit,
    goal: Goal,
    context: GoalEvaluationContext
) => number;  // Returns score (higher = better fit)

export interface GoalEvaluationContext {
    allies: BattleUnit[];
    enemies: BattleUnit[];
    battleMap: Map<string, any>;
    
    // Helper functions
    getDistance: (from: { q: number; r: number }, to: { q: number; r: number }) => number;
    getTarget: (targetId: string) => BattleUnit | undefined;
}

// ═══════════════════════════════════════════════════════════════════════════
// ASSIGNMENT RESULT
// ═══════════════════════════════════════════════════════════════════════════

export interface GoalAssignment {
    unitId: string;
    goal: Goal | null;
    score: number;              // How good is this assignment
    alternatives?: Goal[];      // Other viable goals
}

// ═══════════════════════════════════════════════════════════════════════════
// MANAGER STATE
// ═══════════════════════════════════════════════════════════════════════════

export interface ManagerState {
    goals: Goal[];
    assignments: Map<string, string>;  // unitId → goalId
    goalProgress: Map<string, GoalProgress>;
    
    // Stats (for debugging)
    goalsCreated: number;
    goalsCompleted: number;
    goalsFailed: number;
}

export interface GoalProgress {
    goalId: string;
    startTurn: number;
    unitsAssigned: number;
    targetStillAlive: boolean;
    // Add more tracking as needed
}